/**
 * EvaluationStatusBadge Component
 *
 * Displays a color-coded badge indicating the evaluation status of a job.
 * Provides consistent visual feedback for pass/fail/error/n/a states.
 */

import React from 'react';
import { EvaluationStatus } from '../../types/job';

interface EvaluationStatusBadgeProps {
  status: EvaluationStatus;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

export const EvaluationStatusBadge: React.FC<EvaluationStatusBadgeProps> = ({
  status,
  className = '',
  size = 'small',
}) => {
  // Define badge colors based on status
  const badgeStyles: Record<EvaluationStatus, string> = {
    pass: 'bg-green-100 text-green-800',
    fail: 'bg-red-100 text-red-800',
    error: 'bg-orange-100 text-orange-800',
    'n/a': 'bg-gray-100 text-gray-800',
  };

  // Define size classes
  const sizeClasses: Record<typeof size, string> = {
    small: 'px-2 py-1 text-xs',
    medium: 'px-3 py-1 text-sm',
    large: 'px-4 py-2 text-base',
  };

  // Display text for each status
  const statusText: Record<EvaluationStatus, string> = {
    pass: 'Pass',
    fail: 'Fail',
    error: 'Error',
    'n/a': 'N/A',
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${badgeStyles[status]} ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label={`Evaluation status: ${statusText[status]}`}
    >
      {statusText[status]}
    </span>
  );
};
